import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Paper,
  TextField,
  Grid,
  Container,
  styled
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useOrg } from '../context/OrgContext';
// Styled components for better appearance


const FormContainer = styled(Paper)(({ theme }) => ({
  padding: '0',
  textAlign: 'center',
  maxWidth: 700,
  width: '100%',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  borderRadius: '8px',
  overflow: 'hidden',
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 6px 24px rgba(0,0,0,0.18)'
  }
}));

const FormContent = styled(Box)(({ theme }) => ({
  padding: '32px',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: '24px',
  padding: '14px 30px',
  borderRadius: '8px',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  backgroundColor: '#2E3B55',
  letterSpacing: '1px',
  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
  '&:hover': {
    backgroundColor: '#1E2B45',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 12px rgba(0,0,0,0.2)'
  },
  '&:disabled': {
    backgroundColor: '#7f8a9c'
  }
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #c3c3c3',
  padding: '24px',
  borderRadius: '8px',
  backgroundColor: '#f9f9f9',
  textAlign: 'center',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#2E3B55',
    backgroundColor: '#f0f7ff',
    boxShadow: 'inset 0 0 8px rgba(46, 59, 85, 0.1)'
  }
}));

function W9UploadPage() {
  const { name: orgName, logoUrl, supportEmail } = useOrg();
  const location = useLocation();
  const [token, setToken] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error', 'uploading'
  const [message, setMessage] = useState('');
  
  // Form state
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    // Extract token from URL query parameters
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    // Applicants have no account yet, so the link works with or without a
    // token — we match the upload to their application by email address.
    if (tokenFromUrl) setToken(tokenFromUrl);
    setMessage('Please complete the form and upload your W9 document.');
  }, [location]);

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    setUploadStatus(null); // Clear previous status on new file selection
    setMessage(''); // Clear previous message
  };

  const validateForm = () => {
    if (!name.trim()) return false;
    if (!companyName.trim()) return false;
    if (!email.trim() || !email.includes('@')) return false;
    if (!selectedFile) return false;
    return true;
  };

  const handleUpload = async () => {
    if (!validateForm()) {
      setMessage('Please fill out all required fields and select a file.');
      setUploadStatus('error');
      return;
    }

    setLoading(true);
    setUploadStatus('uploading');
    setMessage('Uploading...');

    const formData = new FormData();
    formData.append('w9', selectedFile);
    if (token) formData.append('token', token); // optional — email match is the fallback
    formData.append('name', name);
    formData.append('companyName', companyName);
    formData.append('email', email);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/upload-w9-tokenized`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        setUploadStatus('success');
        setMessage(result.message || 'W9 uploaded successfully! Thank you for submitting your information.');
        setSelectedFile(null); // Clear selected file on success
      } else {
        setUploadStatus('error');
        setMessage(result.message || 'File upload failed.');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadStatus('error');
      setMessage('An error occurred during upload.');
    } finally {
      setLoading(false);
    }
  };


  // Update document title for better SEO and UX
  useEffect(() => {
    document.title = `W9 Tax Form Submission | ${orgName}`;
    return () => {
      document.title = orgName;
    };
  }, [orgName]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '80vh',
        padding: '20px'
      }}>
        <FormContainer>
          {/* Enhanced Header with Logo */}
          <Box sx={{
            bgcolor: '#2E3B55',
            color: 'white',
            padding: '32px',
            borderRadius: '8px 8px 0 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <img
              src={logoUrl || '/images/logo.png'}
              alt={`${orgName} Logo`}
              style={{
                maxHeight: '80px',
                marginBottom: '16px'
              }}
            />
            <Typography variant="h4" gutterBottom sx={{
              fontWeight: 'bold',
              letterSpacing: '0.5px'
            }}>
              {orgName}
            </Typography>
            
            <Typography variant="h5" sx={{ fontWeight: '500' }}>
              W9 Tax Form Submission
            </Typography>
          </Box>
          
          <FormContent>

          {uploadStatus === 'error' && <Alert severity="error" sx={{ mb: 2, boxShadow: '0 2px 8px rgba(211, 47, 47, 0.2)' }}>{message}</Alert>}
          {uploadStatus === 'success' && <Alert severity="success" sx={{ mb: 2, boxShadow: '0 2px 8px rgba(46, 125, 50, 0.2)' }}>{message}</Alert>}
          {uploadStatus === 'uploading' && <Alert severity="info" sx={{ mb: 2, boxShadow: '0 2px 8px rgba(2, 136, 209, 0.2)' }}>{message}</Alert>}

          {/* Display info message guiding users to fill out the form */}
          {!uploadStatus && (
            <Alert severity="info" sx={{ mb: 3, mt: 1, boxShadow: '0 2px 8px rgba(2, 136, 209, 0.15)' }}>
              Please enter your information and upload your W9 tax form. Use the same
              email address you applied with so we can match it to your application.
            </Alert>
          )}

          {uploadStatus !== 'success' && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  variant="outlined"
                  placeholder="Enter your full name"
                  helperText="Name as it appears on your W9 form"
                  InputProps={{
                    sx: { borderRadius: '8px' }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  variant="outlined"
                  placeholder="Enter your company name"
                  helperText="Your business name as registered"
                  InputProps={{
                    sx: { borderRadius: '8px' }
                  }}
                />
              </Grid>
              
              {/* W9 form example image removed */}
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  variant="outlined"
                  placeholder="Enter your email address"
                  helperText="Email used in your application"
                  InputProps={{
                    sx: { borderRadius: '8px' }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1" align="left" sx={{ mb: 1, fontWeight: '500' }}>
                  W9 Form (PDF or image format) *
                </Typography>
                
                <UploadBox>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.png"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="w9-upload-input"
                  />
                  <label htmlFor="w9-upload-input">
                    <Button
                      variant="contained"
                      component="span"
                      startIcon={<CloudUploadIcon />}
                      disabled={loading}
                      sx={{
                        py: 1.2,
                        px: 3,
                        borderRadius: '8px',
                        fontWeight: 'bold'
                      }}
                    >
                      Select W9 File
                    </Button>
                  </label>

                  <Typography variant="body2" sx={{ mt: 2 }}>
                    {selectedFile ? `Selected file: ${selectedFile.name}` : 'No file selected'}
                  </Typography>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Accepted formats: PDF, DOC, DOCX, JPG, PNG
                  </Typography>
                </UploadBox>
              </Grid>

              <Grid item xs={12}>
                <StyledButton
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={handleUpload}
                  disabled={!validateForm() || loading}
                  size="large"
                >
                  {loading ? <CircularProgress size={24} /> : 'Submit W9 Form'}
                </StyledButton>
              </Grid>
            </Grid>
          )}

          {!token && uploadStatus !== 'error' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          <Box sx={{ mt: 4, bgcolor: '#f5f5f5', p: 3, borderRadius: '0 0 8px 8px', borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="body2" color="text.secondary">
              If you have any questions, please contact <strong>{supportEmail}</strong>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Your information is secure and will only be used for processing your W9 submission.
            </Typography>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'center' }}>
              <img
                src={logoUrl || '/images/logo.png'}
                alt={orgName}
                style={{ height: '30px', opacity: 0.7 }}
              />
            </Box>
          </Box>
          </FormContent>
        </FormContainer>
      </Box>
    </Container>
  );
}

export default W9UploadPage;
